import type { InAppMessage, ControlMessage } from '@braze/web-sdk'
import { isDevEnvironment } from '../windowHelpers/windowHelpers'
import { getBrazeSDK } from '../../Config/Braze/braze.config'

export const getIAMMessageFields = (message: any) => {
  return {
    header: message.header || '',
    text: message.message || '',
    imageUrl: message.imageUrl || '',
    buttons: Array.isArray(message.buttons) ? message.buttons : [],
  }
}

export type BrazeMessage = InAppMessage | ControlMessage

/**
 * Default message handler that auto-shows in-app messages
 * @param braze Braze SDK instance
 * @param message The received message
 */
export const defaultInAppMessageHandler = async (
  message: BrazeMessage
): Promise<boolean> => {
  if (isDevEnvironment()) {
    console.log('[Braze IAM] Handling message with default handler:', message)
  }
  let success = false
  const braze = await getBrazeSDK()
  if (braze) {
    // Braze IAM have a closeMessage method
    if ('closeMessage' in message) {
      if (isDevEnvironment()) {
        console.log('[Braze IAM] Auto-showing in-app message')
      }
      if (typeof braze.showInAppMessage === 'function') {
        try {
          braze.showInAppMessage(message)
          success = true
        } catch (error) {
          console.error('[Braze IAM] Error showing in-app message:', error)
        }
      } else {
        console.warn('[Braze IAM] showInAppMessage method not available')
      }
    }
  } else {
    console.error('[Braze] Trouble connecting to braze:')
  }
  return success
}

/**
 * Creates a subscription to Braze in-app messages
 * @param braze Braze SDK instance
 * @param onMessage Message handler function (defaults to auto-show)
 * @returns subscription ID
 */
export const subscribeToInAppMessages = (
  braze: any,
  onMessage?: (message: BrazeMessage) => void
): string | undefined => {
  if (typeof braze.subscribeToInAppMessage !== 'function') {
    console.warn('[Braze IAM] subscribeToInAppMessage method not available')
    return undefined
  }

  if (isDevEnvironment()) {
    console.log('[Braze IAM] Creating subscription')
  }

  try {
    const subscriptionId = braze.subscribeToInAppMessage((message: BrazeMessage) => {
      if (isDevEnvironment()) {
        console.log('[Braze IAM] Received message:', message)
      }

      try {
        if (onMessage) {
          onMessage(message)
        } else {
          defaultInAppMessageHandler(message)
        }
      } catch (handlerError) {
        console.error('[Braze IAM] Error in message handler:', handlerError)
      }
    })

    if (isDevEnvironment()) {
      console.log('[Braze IAM] Subscription ID:', subscriptionId)
    }

    return subscriptionId
  } catch (subscriptionError) {
    console.error('[Braze IAM] Error creating subscription:', subscriptionError)
    return undefined
  }
}

/**
 * Requests immediate data flush from Braze (fetches pending messages)
 * @param braze Braze SDK instance
 */
export const requestInAppMessageFlush = (braze: any): void => {
  if (typeof braze?.requestImmediateDataFlush === 'function') {
    if (isDevEnvironment()) {
      console.log('[Braze IAM] Requesting immediate data flush')
    }
    braze.requestImmediateDataFlush()
  }
}
